import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ServiceDeliveryLocationsFacade } from '../../../services/service-delivery-locations.facade';
import {
  BandwidthDetails,
  BandwidthEndpoint,
  ServiceDeliveryLocationsService,
  ServiceDeliveryLocationDetails,
  PopSummary,
  ServiceDetail,
  ServiceItem,
  TechnicalPopSummary
} from '../../../services/service-delivery-locations.service';
import { CompanySummary } from '../../../models/company-summary';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyPopInteractionService } from '../../../services/company-pop-interaction.service';
import { AuthenticationService } from '../../../authenticationModule/services/authentication.service';
import { UserLoggedPermissions } from '../../../models/userLoggedPermissions';
import { XcoGroup, XcoGroupItem } from '../models/xco-group';

type BandwidthReceptionEntry = {
  endpoint: BandwidthEndpoint;
  vlan?: string | null;
  sourceItemId: number;
};

type GroupedServiceItem = ServiceItem & {
  groupedReceptions?: BandwidthReceptionEntry[];
};

type ServiceProfileLinkConfig = {
  path: string;
  permission: keyof UserLoggedPermissions;
};

@Component({
  selector: 'app-service-delivery-location-details',
  templateUrl: './service-delivery-location-details.component.html',
  styleUrls: ['./service-delivery-location-details.component.scss']
})
export class ServiceDeliveryLocationDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedCompany: CompanySummary | null = null;
  selectedPop: PopSummary | null = null;
  companyPops: PopSummary[] = [];
  popDetails: ServiceDeliveryLocationDetails | null = null;
  loadingDetails = false;
  errorMessage: string | null = null;

  viewMode: 'xco' | 'service' = 'xco';
  activeServiceKey: string | null = null;
  xcoGroups: XcoGroup[] = [];
  highlightedXcoId: string | null = null;
  highlightedServiceItemId: number | null = null;
  pendingXcoId: string | null = null;
  addpDialogVisible = false;
  addpDialogGroup: XcoGroup | null = null;
  addpDialogItems: XcoGroupItem[] = [];

  private readonly destroy$ = new Subject<void>();
  private groupedServiceItems = new WeakMap<ServiceDetail, GroupedServiceItem[]>();
  private serviceItemIdMap = new Map<number, number>();
  private readonly serviceProfileLinkConfigs: Record<string, ServiceProfileLinkConfig> = {
    qed: { path: 'quincyClientProfile/qed', permission: 'canAccessQedProfile' },
    qrd: { path: 'quincyClientProfile/qrd', permission: 'canAccessQrdProfile' },
    qfd: { path: 'quincyClientProfile/qfd', permission: 'canAccessQfdProfile' },
    qhf: { path: 'quincyClientProfile/qhf', permission: 'canAccessQhfProfile' },
    time_sync_as_a_service: { path: 'timeSyncProfile', permission: 'canAccessTimeSyncProfile' }
  };

  private readonly capturePointerListener = (event: PointerEvent) => this.onDocumentPointerUpCapture(event);

  constructor(
    private readonly facade: ServiceDeliveryLocationsFacade,
    private readonly interaction: CompanyPopInteractionService,
    private readonly service: ServiceDeliveryLocationsService,
    private readonly authentication: AuthenticationService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.facade.selectedCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => this.selectedCompany = company);

    this.facade.selectedPop$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pop => {
        this.selectedPop = pop;
        if (!pop) {
          this.popDetails = null;
          this.activeServiceKey = null;
          this.xcoGroups = [];
          this.groupedServiceItems = new WeakMap<ServiceDetail, GroupedServiceItem[]>();
          this.serviceItemIdMap.clear();
        }
      });

    this.facade.companyPops$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pops => this.companyPops = pops);

    this.facade.popDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe(details => {
        this.popDetails = details;
        if (details) {
          this.activeServiceKey = details.services.length ? details.services[0].key : null;
          this.preloadSymbols(details);
          this.buildGroupedServiceItems(details);
          this.buildXcoGroups();
          this.viewMode = 'xco';
          const pending = this.interaction.consumePendingXco();
          if (pending) {
            this.pendingXcoId = pending;
          }
          this.tryNavigateToPendingXco();
        } else {
          this.xcoGroups = [];
          this.groupedServiceItems = new WeakMap<ServiceDetail, GroupedServiceItem[]>();
          this.serviceItemIdMap.clear();
        }
      });

    this.facade.popDetailsLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => this.loadingDetails = loading);

    this.facade.errorMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => this.errorMessage = message);

    this.interaction.navigateToXco$
      .pipe(takeUntil(this.destroy$))
      .subscribe(xcoId => {
        if (!xcoId) {
          return;
        }
        if (this.popDetails) {
          this.navigateToXco(xcoId);
          this.interaction.consumePendingXco();
        } else {
          this.pendingXcoId = xcoId;
        }
      });
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('pointerup', this.capturePointerListener, true);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.addEventListener('pointerup', this.capturePointerListener, true);
  }

  get activeService(): ServiceDetail | null {
    if (!this.popDetails || !this.activeServiceKey) {
      return null;
    }
    return this.popDetails.services.find(service => service.key === this.activeServiceKey) ?? null;
  }

  onSelectService(serviceKey: string): void {
    this.activeServiceKey = serviceKey;
  }

  serviceLabel(serviceKey: string): string {
    switch (serviceKey.toLowerCase()) {
      case 'qed':
        return 'Snapshot (QED)';
      case 'qrd':
        return 'Raw (QRD)';
      case 'qfd':
        return 'Price & Volume Signals (QFD)';
      case 'qhf':
        return 'Volume Signals (QHF)';
      case 'news':
        return 'News';
      case 'time_sync_as_a_service':
        return 'Precision Time Exchange';
      case 'bandwidth':
        return 'Bandwidth';
      default:
        return serviceKey.toUpperCase();
    }
  }

  serviceProfileLink(serviceKey: string): (string | number)[] | null {
    const uniqid = this.selectedCompany?.uniqid;
    if (!uniqid || !serviceKey) {
      return null;
    }
    const config = this.serviceProfileLinkConfigs[serviceKey.toLowerCase()];
    if (!config || !this.hasPermission(config.permission)) {
      return null;
    }
    const segments = config.path.split('/').filter(Boolean);
    return ['/', uniqid, ...segments];
  }

  formatTechnicalPops(item: ServiceItem): string {
    if (!item.crossConnects?.length) {
      return '';
    }
    return item.crossConnects
      .map(cc => cc.technicalPop)
      .filter((tp): tp is TechnicalPopSummary => !!tp)
      .map(tp => tp.technicalId || tp.name || '')
      .filter((value, index, array) => value && array.indexOf(value) === index)
      .join(', ');
  }

  private hasPermission(permission: keyof UserLoggedPermissions): boolean {
    return !!this.authentication.userCurrent?.permissions?.[permission];
  }

  getCrossConnectLinks(item: ServiceItem): { id: string; label: string }[] {
    if (!item.crossConnects?.length) {
      return [];
    }
    const seen = new Set<string>();
    const links: { id: string; label: string }[] = [];
    item.crossConnects.forEach(cc => {
      if (!cc.crossConnectId) {
        return;
      }
      const tp = cc.technicalPop?.technicalId;
      const label = tp ? `${cc.crossConnectId} (${tp})` : cc.crossConnectId;
      if (!seen.has(label)) {
        seen.add(label);
        links.push({ id: cc.crossConnectId, label });
      }
    });
    return links;
  }

  isBandwidthItem(item: ServiceItem): boolean {
    return (item.service || '').toLowerCase() === 'bandwidth';
  }

  bandwidthDetailsFor(item: ServiceItem): BandwidthDetails | null {
    return item.bandwidthDetails ?? null;
  }

  bandwidthInjection(details: BandwidthDetails | null): BandwidthEndpoint | null {
    return details?.injection ?? null;
  }

  bandwidthReceptions(details: BandwidthDetails | null): BandwidthEndpoint[] {
    return details?.receptions ?? [];
  }

  defaultTypeLabel(item: ServiceItem): string {
    return this.serviceLabel(item.service || '');
  }

  isMarketDataItem(item: ServiceItem): boolean {
    return !this.isBandwidthItem(item);
  }

  formatTypeMeta(item: ServiceItem): string {
    if (!this.isMarketDataItem(item)) {
      return '';
    }
    const carrier = item.product?.carrier ?? '';
    if (!carrier) {
      return '';
    }
    return item.isEncrypted ? `${carrier} & Encrypted` : carrier;
  }

  carrierSuffix(item: ServiceItem): string {
    if (!this.isMarketDataItem(item)) {
      return '';
    }
    const carrier = item.product?.carrier ?? '';
    return carrier ? `(Carrier: ${carrier})` : '';
  }

  crossConnectLabel(count: number): string {
    if (count === 1) {
      return 'XCO';
    }
    return 'XCOs';
  }

  serviceTagClass(serviceKey: string): string {
    const normalized = (serviceKey || '').toLowerCase();
    if (normalized === 'bandwidth') {
      return 'service-tag--bandwidth';
    }
    return 'service-tag--market';
  }

  getTechnicalPopForXco(item: ServiceItem, xcoId: string): string {
    const entry = item.crossConnects?.find(c => c.crossConnectId === xcoId);
    if (!entry?.technicalPop) {
      return '';
    }
    return entry.technicalPop.technicalId || entry.technicalPop.name || '';
  }

  getDatacenterRefForXco(item: ServiceItem, xcoId: string): string {
    const entry = item.crossConnects?.find(c => c.crossConnectId === xcoId);
    return entry?.datacenterRef ?? '';
  }

  datacenterRefForGroup(group: { xcoId: string; items: { item: ServiceItem }[] }): string {
    if (!group?.items?.length) {
      return '';
    }
    for (const entry of group.items) {
      const ref = this.getDatacenterRefForXco(entry.item, group.xcoId);
      if (ref) {
        return ref;
      }
    }
    return '';
  }

  navigateToXco(xcoId: string): void {
    this.viewMode = 'xco';
    this.highlightedXcoId = xcoId;
    setTimeout(() => {
      const element = document.getElementById(`xco-${xcoId}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  navigateToServiceItem(serviceKey: string, item: ServiceItem): void {
    this.viewMode = 'service';
    this.onSelectService(serviceKey);
    const targetId = this.serviceItemIdMap.get(item.id) ?? item.id;
    this.highlightedServiceItemId = targetId;
    setTimeout(() => {
      const target = document.getElementById(`service-item-${targetId}`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    setTimeout(() => {
      if (this.highlightedServiceItemId === targetId) {
        this.highlightedServiceItemId = null;
      }
    }, 3000);
  }

  getDisplayItems(service: ServiceDetail): GroupedServiceItem[] {
    if (!service) {
      return [];
    }
    return this.groupedServiceItems.get(service) ?? (service.items as GroupedServiceItem[]);
  }

  private onDocumentPointerUpCapture(event: PointerEvent): void {
    const host = this.elementRef?.nativeElement;
    if (!host) {
      return;
    }
    const composedPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const candidates: HTMLElement[] = [];
    if (composedPath.length) {
      composedPath.forEach(entry => {
        if (entry instanceof HTMLElement) {
          candidates.push(entry);
        }
      });
    }
    if (candidates.length === 0) {
      const fallback = event.target as HTMLElement | null;
      if (fallback) {
        candidates.push(fallback);
      }
    }

    const link = candidates.find(el => el.classList?.contains('xco-link') && el.hasAttribute('data-xco-id'));
    if (!link || !host.contains(link)) {
      return;
    }
    const xcoId = link.getAttribute('data-xco-id');
    if (!xcoId) {
      return;
    }
    event.preventDefault();
    this.ngZone.run(() => this.navigateToXco(xcoId));
  }

  private loadSymbolsForItem(item: ServiceItem): void {
    if (!this.selectedCompany || !this.selectedPop) {
      return;
    }
    item.symbolsLoading = true;
    const channelIds = (item.channels || [])
      .map(channel => channel.channelId)
      .filter((id): id is number => typeof id === 'number');
    if (!channelIds.length) {
      item.symbols = [];
      item.symbolsLoaded = true;
      item.symbolsLoading = false;
      return;
    }
    this.service.getItemSymbols(
      this.selectedCompany.id,
      this.selectedPop.id,
      item.id,
      channelIds
    ).subscribe({
      next: symbols => {
        item.symbols = symbols;
        item.symbolsLoaded = true;
        item.symbolsLoading = false;
      },
      error: () => {
        item.symbolsLoading = false;
      }
    });
  }

  getReceptionEntries(item: ServiceItem): BandwidthReceptionEntry[] {
    const candidate = item as GroupedServiceItem;
    if (candidate.groupedReceptions?.length) {
      return candidate.groupedReceptions;
    }
    const bandwidth = this.bandwidthDetailsFor(candidate);
    if (!bandwidth?.receptions?.length) {
      return [];
    }
    return bandwidth.receptions.map(endpoint => ({
      endpoint,
      vlan: bandwidth.vlan,
      sourceItemId: candidate.id
    }));
  }

  private buildReceptionEntries(item: ServiceItem): BandwidthReceptionEntry[] {
    const bandwidth = item.bandwidthDetails;
    if (!bandwidth?.receptions?.length) {
      return [];
    }
    return bandwidth.receptions.map(endpoint => ({
      endpoint,
      vlan: bandwidth.vlan,
      sourceItemId: item.id
    }));
  }

  private buildXcoGroups(): void {
    if (!this.popDetails) {
      this.xcoGroups = [];
      return;
    }
    const groups: Record<string, XcoGroupItem[]> = {};
    this.popDetails.services.forEach(service => {
      const items = this.getDisplayItems(service);
      items.forEach(item => {
        item.crossConnects?.forEach(cc => {
          if (!cc.crossConnectId) {
            return;
          }
          if (!groups[cc.crossConnectId]) {
            groups[cc.crossConnectId] = [];
          }
          groups[cc.crossConnectId].push({
            serviceKey: service.key,
            serviceLabel: this.serviceLabel(service.key),
            item
          });
        });
      });
    });

    this.xcoGroups = Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map(id => ({
        xcoId: id,
        items: groups[id]
      }));
  }

  private getAddpEligibleItems(group: XcoGroup): XcoGroupItem[] {
    return group.items.filter(entry =>
      this.isMarketDataItem(entry.item) && entry.item.product?.addpSelfServe
    );
  }

  private buildGroupedServiceItems(details: ServiceDeliveryLocationDetails): void {
    this.groupedServiceItems = new WeakMap<ServiceDetail, GroupedServiceItem[]>();
    this.serviceItemIdMap = new Map<number, number>();

    details.services.forEach((service, index) => {
      if (service.key.toLowerCase() !== 'bandwidth') {
        const items = service.items.map(item => {
          this.serviceItemIdMap.set(item.id, item.id);
          return item as GroupedServiceItem;
        });
        this.groupedServiceItems.set(service, items);
        return;
      }

      const buckets = new Map<string, GroupedServiceItem>();
      service.items.forEach(item => {
        const bandwidth = item.bandwidthDetails;
        const key = bandwidth?.etreeId ?? item.product?.name ?? `${index}-${item.id}`;
        if (!buckets.has(key)) {
          buckets.set(key, {
            ...item,
            groupedReceptions: []
          });
        }
        const target = buckets.get(key)!;
        if (!this.serviceItemIdMap.has(target.id)) {
          this.serviceItemIdMap.set(target.id, target.id);
        }
        this.serviceItemIdMap.set(item.id, target.id);

        const entries = this.buildReceptionEntries(item);
        entries.forEach(entry => {
          if (!target.groupedReceptions?.some(existing => existing.endpoint.crossConnectId === entry.endpoint.crossConnectId)) {
            target.groupedReceptions?.push(entry);
          }
        });

        const existingCrossConnects = target.crossConnects ?? [];
        (item.crossConnects ?? []).forEach(cc => {
          if (!existingCrossConnects.some(existing => existing.crossConnectId === cc.crossConnectId)) {
            existingCrossConnects.push(cc);
          }
        });
        target.crossConnects = existingCrossConnects;
      });

      this.groupedServiceItems.set(service, Array.from(buckets.values()));
    });
  }

  private tryNavigateToPendingXco(): void {
    if (!this.pendingXcoId || !this.popDetails) {
      return;
    }
    const xcoId = this.pendingXcoId;
    this.pendingXcoId = null;
    this.navigateToXco(xcoId);
  }

  onCrossConnectLinkClick(xcoId: string, event: Event): void {
    event.preventDefault();
    this.navigateToXco(xcoId);
  }

  onBandwidthEndpointClick(endpoint: BandwidthEndpoint | null | undefined, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!endpoint?.crossConnectId) {
      return;
    }
    const targetPopId = endpoint.popId;
    if (!targetPopId || targetPopId === this.selectedPop?.id) {
      this.navigateToXco(endpoint.crossConnectId);
      return;
    }
    const targetPop = this.companyPops.find(pop => pop.id === targetPopId);
    if (!targetPop) {
      this.navigateToXco(endpoint.crossConnectId);
      return;
    }
    this.pendingXcoId = endpoint.crossConnectId;
    this.facade.selectPop(targetPop);
  }

  channelStatusClass(status?: string | null): string {
    if (!status) {
      return '';
    }
    const normalized = status.toLowerCase();
    if (normalized === 'configured') {
      return 'status-pill status-pill--configured';
    }
    if (normalized === 'deconfigured') {
      return 'status-pill status-pill--deconfigured';
    }
    if (normalized.includes('deconfig')) {
      return 'status-pill status-pill--warning';
    }
    if (normalized === 'new' || normalized.includes('requested') || normalized.includes('scheduled')) {
      return 'status-pill status-pill--pending';
    }
    return 'status-pill';
  }

  symbolColumnLabel(item: ServiceItem): string {
    switch ((item.service || '').toLowerCase()) {
      case 'qfd':
        return 'QFD Symbol';
      case 'qrd':
        return 'QRD Symbol';
      case 'qed':
        return 'QED Symbol';
      case 'qhf':
        return 'QHF Symbol';
      default:
        return 'Symbol';
    }
  }

  formatLayer(layer: unknown): string {
    if (typeof layer === 'string') {
      return layer.toUpperCase();
    }
    if (layer === null || layer === undefined) {
      return '—';
    }
    return String(layer);
  }

  private preloadSymbols(details: ServiceDeliveryLocationDetails): void {
    details.services.forEach(service => {
      service.items.forEach(item => {
        if (!item.symbolsLoaded) {
          this.loadSymbolsForItem(item);
        }
      });
    });
  }

  isAddpEligible(group: XcoGroup): boolean {
    if (!this.popDetails?.commercialPop?.addpSelfServe) {
      return false;
    }

    return this.getAddpEligibleItems(group).length > 0;
  }

  openAddpDialog(group: XcoGroup): void {
    if (!this.popDetails?.commercialPop) {
      return;
    }
    const eligibleItems = this.getAddpEligibleItems(group);
    if (!eligibleItems.length) {
      return;
    }
    this.addpDialogGroup = group;
    this.addpDialogItems = eligibleItems;
    this.addpDialogVisible = true;
  }

  onAddpDialogVisibilityChange(visible: boolean): void {
    this.addpDialogVisible = visible;
    if (!visible) {
      this.addpDialogGroup = null;
      this.addpDialogItems = [];
    }
  }
}
